
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getUserServiceProviders } from "@/services/providers/providerOwnerService";
import { useAuth } from "@/contexts/auth";
import { useSubscription } from "@/hooks/subscription";
import UserServiceCard from "@/components/UserServiceCard";
import { UserListingsHeader } from "@/components/user-listings/UserListingsHeader";
import { UserListingsAlerts } from "@/components/user-listings/UserListingsAlerts";
import { UserListingsEmpty } from "@/components/user-listings/UserListingsEmpty";
import { debugLogger } from "@/utils/debug";
import { useTranslation } from "react-i18next";
import PageHero from "@/components/layout/PageHero";

const UserListingsContainer = () => {
  const { user } = useAuth();
  const { t } = useTranslation('common');
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const navigate = useNavigate();
  const { data: subscriptionData } = useSubscription();
  const isFreeTier = !subscriptionData?.subscribed || subscriptionData?.tier === 'free_tier';
  const isPremiumTier = subscriptionData?.tier === 'premium_tier';
  
  useEffect(() => {
    // If user is not authenticated, redirect to auth page
    if (user === null) {
      navigate("/auth?redirect=/user/listings");
    }
  }, [user, navigate]);
  
  const { data: userListings = [], refetch } = useQuery({
    queryKey: ['userListings', 'all-statuses', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      try {
        const data = await getUserServiceProviders(user.id, "all");
        debugLogger.log("User listings fetched:", data);
        return data || [];
      } catch (error) {
        console.error("Error fetching user listings:", error);
        throw error;
      }
    },
    enabled: !!user,
    refetchOnMount: "always",
    staleTime: 0,
  });

  const handleUpdateListings = () => {
    debugLogger.log("Refreshing listings after deletion or update");
    refetch();
  };

  const approvedListingsCount = userListings.filter(listing => listing.status === 'approved').length;
  const filteredListings = statusFilter === "all"
    ? userListings
    : userListings.filter((listing) => listing.status === statusFilter);
  
  // Show alert based on subscription tier
  const showFreeTierAlert = isFreeTier && (approvedListingsCount > 0 || userListings.length > 0);
  
  // Determine if user can add a new listing
  // Premium tier users can always add new listings
  // Free tier or WhatsApp tier users can only add if they have no listings
  const canAddNewListing = isPremiumTier || (userListings.length === 0);

  // Check if still loading auth state
  if (user === undefined) {
    return <div className="flex min-h-screen items-center justify-center bg-brand-mist">{t("listings.loading")}</div>;
  }
  
  // If user is null (not authenticated), return null as the redirect will happen in useEffect
  if (user === null) {
    return null;
  }

  return (
    <div className="bg-brand-mist">
      <PageHero
        title={t("listings.title")}
        description={t("listings.description")}
        contentClassName="py-12 md:py-16"
      />
      <div className="container mx-auto px-4 py-8 md:py-10">
        <UserListingsHeader 
          canAddNewListing={canAddNewListing}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          showTitle={false}
        />
        
        <UserListingsAlerts 
          showFreeTierAlert={showFreeTierAlert}
          approvedListingsCount={approvedListingsCount}
        />
        
        {filteredListings.length === 0 ? (
          <UserListingsEmpty 
            statusFilter={statusFilter}
            canAddNewListing={canAddNewListing}
            onClearFilter={() => setStatusFilter("all")}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredListings.map((listing) => {
              const createdAt = 'created_at' in listing && typeof listing.created_at === 'string'
                ? listing.created_at
                : new Date().toISOString();

              return (
                <UserServiceCard
                  key={listing.id}
                  service={{
                    id: listing.id || '',
                    name: listing.name || 'Untitled Listing',
                    description: listing.description || 'No description available',
                    category: listing.category || 'Uncategorized',
                    categories: listing.categories || [],
                    location: listing.suburbs && listing.suburbs.length > 0 ? listing.suburbs.join(', ') : 'No location specified',
                    suburbs: listing.suburbs || [],
                    status: listing.status || 'unknown',
                    created_at: createdAt,
                    images: listing.image_url ? [listing.image_url] : []
                  }}
                  subscriptionData={subscriptionData}
                  onDelete={handleUpdateListings}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserListingsContainer;
